import { ReactNode, createContext, useContext, useState } from 'react'
import { useOutletContext } from 'react-router-dom'

import { omit, pick } from 'lodash'

import { Result } from 'modules/core/lib/handleMutation'

import { useExpensableActions } from 'modules/expenses/hooks/useExpensableActions'
import { RouteContextParams } from 'modules/expenses/lib/RouteContextParams'
import {
  everyReimbursableCurrencyAllowed,
  showNotAllowedCurrenciesMessage,
} from 'modules/expenses/lib/expensables/expensable'
import { trackEditedExpensable } from 'modules/expenses/lib/expensables/expensableTracking'
import { Expensable } from 'modules/expenses/types/Expensable'

export enum ConfirmationModal {
  GlobalApprove,
  GlobalReject,
  Reject,
  RestartApprovalFlow,
  AddToPayroll,
  AddToPayment,
  RemoveFromPayroll,
  RequestChanges,
}

export type SubmitFn = (
  identifier: string,
  action: Promise<Result<{ expensables?: Expensable[] | null }>>
) => Promise<void>

interface BulkActionsContext {
  globalApprove: () => void
  reject: () => void
  globalReject: () => void
  restartApprovalFlow: () => void
  addToPayroll: () => void
  addToPayment: () => void
  removeFromPayroll: () => void
  approve: () => Promise<void>
  removeFromGroup: () => Promise<void>
  setToPaid: () => Promise<void>
  revertPaid: () => Promise<void>
  closeModal: () => void
  submit: SubmitFn
  modal: ConfirmationModal | null
  submitting: boolean
  requestChanges: () => void
}

interface BulkActionsProviderProps {
  expensable?: Expensable
  children: ReactNode
}

const BulkActions = createContext<BulkActionsContext>({
  globalApprove: () => null,
  reject: () => null,
  globalReject: () => null,
  restartApprovalFlow: () => null,
  addToPayroll: () => null,
  addToPayment: () => null,
  removeFromPayroll: () => null,
  approve: () => Promise.resolve(),
  removeFromGroup: () => Promise.resolve(),
  setToPaid: () => Promise.resolve(),
  revertPaid: () => Promise.resolve(),
  submit: () => Promise.resolve(),
  closeModal: () => null,
  modal: null,
  submitting: false,
  requestChanges: () => null,
})

export const BulkActionsProvider = ({ expensable, children }: BulkActionsProviderProps) => {
  const { section } = useOutletContext<RouteContextParams>()

  const [submitting, setSubmitting] = useState(false)
  const [modal, setModal] = useState<ConfirmationModal | null>(null)

  const { onApprove, onRemoveFromGroup, onSetToPaid, onRevertPaid } = useExpensableActions()

  const submit: SubmitFn = async (identifier, action) => {
    if (!expensable) return
    setModal(null)
    setSubmitting(true)

    const handled = await action

    if (handled.success) {
      const updatedExpensable = handled.data?.expensables?.[0]

      if (updatedExpensable) {
        trackEditedExpensable(
          { ...expensable, ...updatedExpensable },
          {
            action: identifier,
            previousStatus: expensable.status,
            context: section,
          }
        )
      }
    }

    setSubmitting(false)
  }

  const context: BulkActionsContext = {
    globalApprove: () => setModal(ConfirmationModal.GlobalApprove),
    reject: () => setModal(ConfirmationModal.Reject),
    globalReject: () => setModal(ConfirmationModal.GlobalReject),
    restartApprovalFlow: () => setModal(ConfirmationModal.RestartApprovalFlow),
    addToPayment: () => setModal(ConfirmationModal.AddToPayment),
    addToPayroll: () =>
      expensable && everyReimbursableCurrencyAllowed([expensable])
        ? setModal(ConfirmationModal.AddToPayroll)
        : showNotAllowedCurrenciesMessage(),
    removeFromPayroll: () => setModal(ConfirmationModal.RemoveFromPayroll),
    approve: () => (expensable ? submit('approve', onApprove([expensable.id])) : Promise.resolve()),
    removeFromGroup: () =>
      expensable
        ? submit('remove_from_group', onRemoveFromGroup([expensable.id]))
        : Promise.resolve(),
    setToPaid: () =>
      expensable ? submit('set_to_paid', onSetToPaid([expensable.id])) : Promise.resolve(),
    revertPaid: () =>
      expensable ? submit('revert_paid', onRevertPaid([expensable.id])) : Promise.resolve(),
    closeModal: () => setModal(null),
    modal,
    submit,
    submitting,
    requestChanges: () => setModal(ConfirmationModal.RequestChanges),
  }

  return <BulkActions.Provider value={context}>{children}</BulkActions.Provider>
}

export const useBulkActions = () => {
  const context = useContext(BulkActions)
  return omit(context, ['modal', 'closeModal', 'submit'])
}

export const useConfirmationModal = () => {
  const context = useContext(BulkActions)
  return pick(context, ['modal', 'closeModal', 'submit'])
}
